import axios from 'axios';
import { useAuthStore } from '@/stores/auth.store';
import type { LoginResponse } from '@/types/auth.types';

/**
 * Dedicated axios instance for tenant refresh calls only.
 * No interceptors — avoids the 401 → refresh loop and reads the raw envelope.
 */
const refreshClient = axios.create({
  baseURL: '/api/v1',
  timeout: 10_000,
  withCredentials: true,
});

/**
 * In-flight refresh promise — concurrent callers (401 interceptor + proactive timer)
 * share one outgoing request and all receive the same resolved/rejected result.
 */
let activeRefresh: Promise<string> | null = null;

/**
 * Refreshes the tenant access token using the stored refresh token.
 *
 * Returns the new access token on success.
 * Clears auth and throws on failure (expired / revoked refresh token).
 * Concurrent calls before the first settles share the same in-flight promise.
 */
export function refreshTenantAccessToken(): Promise<string> {
  if (activeRefresh !== null) return activeRefresh;

  activeRefresh = performRefresh().finally(() => {
    activeRefresh = null;
  });

  return activeRefresh;
}

async function performRefresh(): Promise<string> {
  const { refreshToken, clearAuth, rotateTokens } = useAuthStore.getState();

  if (!refreshToken) {
    clearAuth();
    throw new Error('No refresh token available');
  }

  // Backend returns { data: LoginResponse, meta: {...} } — no interceptor unwraps here
  const resp = await refreshClient.post<{ data: LoginResponse }>(
    '/auth/refresh',
    { refreshToken },
  );

  const tokens = resp.data.data;
  if (!tokens?.accessToken || !tokens?.refreshToken) {
    clearAuth();
    throw new Error('Malformed refresh response');
  }

  rotateTokens(tokens.accessToken, tokens.refreshToken);
  return tokens.accessToken;
}
